import { api, unwrap, unwrapPaged } from '../apiClient';
import type {
  AgentDailyTotal,
  AuditAction,
  AuditLogEntry,
  ChequeRegisterEntry,
  CollectionPayload,
  CollectionReport,
  DailySummary,
  DashboardData,
  Invoice,
  PageMeta,
  Receipt,
  ReceiptSummary,
  ShopDailyBreakdown,
} from '@/domain/types';

type Paged<T> = Promise<{ rows: T[]; meta: PageMeta }>;

// --- Invoices ---------------------------------------------------------------

export interface InvoiceListParams {
  q?: string;
  shopId?: number;
  routeId?: number;
  agentId?: number;
  status?: string;
  outstandingOnly?: boolean;
  overdueOnly?: boolean;
  dueFrom?: string;
  dueTo?: string;
  sortBy?: 'dueDate' | 'amount' | 'outstanding' | 'invoiceNo' | 'createdAt';
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export interface InvoiceInput {
  shopId: number;
  invoiceNo: string;
  description?: string | null;
  amountCents: number;
  invoiceDate?: string;
  dueDate: string;
}

export interface ImportResult {
  totalRows: number;
  validRows: number;
  imported: number;
  skipped: number;
  dryRun: boolean;
  errors: Array<{ row: number; invoiceNo?: string; errors: string[] }>;
}

export const invoicesApi = {
  list(params: InvoiceListParams): Paged<Invoice> {
    return unwrapPaged<Invoice>(api.get('/invoices', { params }));
  },
  get(id: number): Promise<Invoice & { payments: unknown[] }> {
    return unwrap(api.get(`/invoices/${id}`));
  },
  create(input: InvoiceInput): Promise<Invoice> {
    return unwrap<Invoice>(api.post('/invoices', input));
  },
  update(
    id: number,
    input: Partial<Omit<InvoiceInput, 'invoiceNo'>> & { reason?: string },
  ): Promise<Invoice> {
    return unwrap<Invoice>(api.patch(`/invoices/${id}`, input));
  },
  cancel(id: number, reason: string): Promise<Invoice> {
    return unwrap<Invoice>(api.post(`/invoices/${id}/cancel`, { reason }));
  },

  import(file: File, options: { dryRun?: boolean; allowPartial?: boolean }): Promise<ImportResult> {
    const form = new FormData();
    form.append('file', file);

    return unwrap<ImportResult>(
      api.post('/invoices/import', form, {
        params: options,
        // Let the browser set the multipart boundary; a hand-written
        // Content-Type here produces an unparseable body.
        headers: { 'Content-Type': undefined },
      }),
    );
  },
};

// --- Collections ------------------------------------------------------------

export interface CollectionListParams {
  agentId?: number;
  shopId?: number;
  routeId?: number;
  from?: string;
  to?: string;
  status?: 'ACTIVE' | 'VOID' | 'all';
  receiptNo?: number;
  page?: number;
  pageSize?: number;
}

export const collectionsApi = {
  list(params: CollectionListParams): Paged<ReceiptSummary> {
    return unwrapPaged<ReceiptSummary>(api.get('/collections', { params }));
  },

  get(id: number): Promise<Receipt> {
    return unwrap<Receipt>(api.get(`/collections/${id}`));
  },

  /**
   * Records one shop visit.
   *
   * `clientUuid` in the payload and the `Idempotency-Key` header together make
   * this safe to retry: a resubmission returns the original receipt rather
   * than taking the money twice. The key is the same UUID, so a browser retry
   * after a dropped response cannot double-book.
   */
  create(payload: CollectionPayload): Promise<Receipt> {
    return unwrap<Receipt>(
      api.post('/collections', payload, {
        headers: { 'Idempotency-Key': payload.clientUuid },
      }),
    );
  },

  void(id: number, reason: string): Promise<Receipt> {
    return unwrap<Receipt>(api.post(`/collections/${id}/void`, { reason }));
  },
};

// --- Reports ----------------------------------------------------------------

export const reportsApi = {
  agentDaily(agentId: number, date: string): Promise<CollectionReport> {
    return unwrap<CollectionReport>(api.get('/reports/agent-daily', { params: { agentId, date } }));
  },

  /**
   * The server-rendered PDF. Fetched as a blob rather than linked directly so
   * the Authorization header is sent — the report endpoint is authenticated,
   * and a plain <a href> would arrive without a token.
   */
  async agentDailyPdf(agentId: number, date: string): Promise<Blob> {
    const response = await api.get('/reports/agent-daily/pdf', {
      params: { agentId, date, disposition: 'attachment' },
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  agentTotals(date: string): Promise<AgentDailyTotal[]> {
    return unwrap<AgentDailyTotal[]>(api.get('/reports/agent-totals', { params: { date } }));
  },

  shopBreakdown(date: string, agentId?: number): Promise<ShopDailyBreakdown[]> {
    return unwrap<ShopDailyBreakdown[]>(
      api.get('/reports/shop-breakdown', { params: { date, agentId } }),
    );
  },

  summary(from: string, to: string, agentId?: number): Promise<DailySummary> {
    return unwrap<DailySummary>(api.get('/reports/summary', { params: { from, to, agentId } }));
  },

  routeDaily(date: string): Promise<
    Array<{
      routeId: number | null;
      routeName: string;
      agentName: string | null;
      receiptCount: number;
      totalCents: number;
    }>
  > {
    return unwrap(api.get('/reports/route-daily', { params: { date } }));
  },

  cheques(from: string, to: string, agentId?: number): Promise<ChequeRegisterEntry[]> {
    return unwrap<ChequeRegisterEntry[]>(
      api.get('/reports/cheques', { params: { from, to, agentId } }),
    );
  },
};

// --- Audit log ----------------------------------------------------------------

export interface AuditLogListParams {
  from?: string;
  to?: string;
  userId?: number;
  action?: AuditAction;
  page?: number;
  pageSize?: number;
}

export const auditApi = {
  list(params: AuditLogListParams): Paged<AuditLogEntry> {
    return unwrapPaged<AuditLogEntry>(api.get('/audit-logs', { params }));
  },
};

// --- Dashboard --------------------------------------------------------------

export const dashboardApi = {
  main(from: string, to: string): Promise<DashboardData> {
    return unwrap<DashboardData>(api.get('/dashboard', { params: { from, to } }));
  },
};

// --- Mobile / agent ---------------------------------------------------------

export const agentApi = {
  summary(date?: string): Promise<{
    date: string;
    receiptCount: number;
    totalCents: number;
    cashCents: number;
    chequeCents: number;
    pendingInvoiceCount: number;
    pendingOutstandingCents: number;
  }> {
    return unwrap(api.get('/mobile/summary', { params: { date } }));
  },
};
